// Workout Module Types - Internal types for the workout feature
import { Exercise, WorkoutSet, AppSettings, WorkoutSession } from '../../../types';
import { PersonalRecord } from '../../../services/prService';

// ============================================================
// STATE TYPES
// ============================================================

export interface RestTimerState {
    active: boolean;
    endTime: number | null;
    totalTime: number;
    timeLeft: number;
}

export interface NumpadState {
    isOpen: boolean;
    target: 'weight' | 'reps' | null;
    value: string;
}

export interface WorkoutState {
    // === Core Data ===
    exercises: Exercise[];
    currentExerciseIndex: number;

    // === Time Tracking (Stable Timestamps) ===
    startTimestamp: number;
    totalPausedTime: number;
    lastPauseTimestamp: number | null;
    isPaused: boolean;

    // === Rest Timer ===
    restTimer: RestTimerState;

    // === UI State ===
    showSettings: boolean;
    showExerciseSelector: boolean;
    showQuickForm: boolean;
    showExerciseLibrary: boolean;
    isDrawerOpen: boolean;
    numpad: NumpadState;

    // === Flow Modals ===
    showGoalSelector: boolean;
    showWarmup: boolean;
    showCooldown: boolean;
    showWaterReminder: boolean;
    showTutorial: boolean;
    showAICoach: boolean;

    // === Celebration State ===
    tutorialExercise: string | null;
    showConfetti: boolean;
    showPRCelebration: PersonalRecord | null;

    // === Settings (Cached from App) ===
    appSettings: AppSettings;

    // === Ghost Values (Previous Workout Data) ===
    previousExerciseData: WorkoutSet[] | null;

    // === Haptic Trigger ===
    pendingHaptic: 'REST_END' | 'SET_COMPLETE' | null;
}

// ============================================================
// ACTION TYPES
// ============================================================

// --- Exercise Actions ---
export type ExerciseAction =
    | { type: 'ADD_EXERCISE'; payload: Exercise }
    | { type: 'REMOVE_EXERCISE'; payload: number }
    | { type: 'REORDER_EXERCISES'; payload: Exercise[] }
    | { type: 'CHANGE_EXERCISE'; payload: number }
    | { type: 'RENAME_EXERCISE'; payload: { index: number; name: string } }
    | {
        type: 'UPDATE_EXERCISE_META'; payload: {
            index: number;
            muscleGroup?: string;
            tempo?: string;
            targetRestTime?: number;
            tutorialText?: string
        }
    };

// --- Set Actions ---
export type SetAction =
    | { type: 'UPDATE_SET'; payload: { field: 'weight' | 'reps'; value: number } }
    | { type: 'COMPLETE_SET' }
    | { type: 'UNDO_LAST_SET' }
    | { type: 'UPDATE_SET_NOTES'; payload: string | undefined }
    | { type: 'UPDATE_SET_RPE'; payload: number | undefined }
    | { type: 'COPY_PREVIOUS_SET' }
    | { type: 'DUPLICATE_SET' }
    | { type: 'EDIT_SPECIFIC_SET'; payload: { exerciseIndex: number; setIndex: number; updates: Partial<{ weight: number; reps: number }> } }
    | { type: 'DELETE_SET'; payload: { exerciseIndex: number; setIndex: number } };

// --- Timer Actions ---
export type TimerAction =
    | { type: 'TOGGLE_PAUSE' }
    | { type: 'SKIP_REST' }
    | { type: 'ADD_REST_TIME'; payload: number }
    | { type: 'SET_REST_TIME'; payload: number }
    | { type: 'SYNC_REST_TIMER' }; // Only for rest timer, not workout timer

// --- UI Actions ---
export type UIAction =
    | { type: 'OPEN_NUMPAD'; payload: 'weight' | 'reps' }
    | { type: 'CLOSE_NUMPAD' }
    | { type: 'NUMPAD_INPUT'; payload: string }
    | { type: 'NUMPAD_DELETE' }
    | { type: 'NUMPAD_SUBMIT' }
    | { type: 'TOGGLE_DRAWER'; payload: boolean }
    | { type: 'TOGGLE_SETTINGS'; payload: boolean }
    | { type: 'OPEN_SELECTOR' }
    | { type: 'CLOSE_SELECTOR' }
    | { type: 'OPEN_QUICK_FORM' }
    | { type: 'CLOSE_QUICK_FORM' }
    | { type: 'OPEN_EXERCISE_LIBRARY' }
    | { type: 'CLOSE_EXERCISE_LIBRARY' }
    | { type: 'OPEN_AI_COACH' }
    | { type: 'CLOSE_AI_COACH' };

// --- Modal Actions ---
export type ModalAction =
    | { type: 'SET_MODAL_STATE'; payload: { modal: ModalType; isOpen: boolean } }
    | { type: 'SHOW_TUTORIAL'; payload: string }
    | { type: 'SHOW_PR_CELEBRATION'; payload: PersonalRecord }
    | { type: 'HIDE_PR_CELEBRATION' }
    | { type: 'HIDE_CONFETTI' };

// --- Settings & Data Actions ---
export type DataAction =
    | { type: 'SET_EXERCISES'; payload: Exercise[] }
    | { type: 'UPDATE_SETTINGS'; payload: Partial<AppSettings['workoutSettings']> }
    | { type: 'SET_PREVIOUS_DATA'; payload: WorkoutSet[] | null }
    | { type: 'CLEAR_PENDING_HAPTIC' };

// Combined Action Type
export type WorkoutAction =
    | ExerciseAction
    | SetAction
    | TimerAction
    | UIAction
    | ModalAction
    | DataAction;

// Modal Type
export type ModalType = 'goal' | 'warmup' | 'cooldown' | 'water' | 'tutorial' | 'aicoach';

// ============================================================
// CONTEXT TYPES
// ============================================================

export interface WorkoutContextValue {
    state: WorkoutState;
    dispatch: React.Dispatch<WorkoutAction>;
}

export interface WorkoutDerivedValue {
    currentExercise: Exercise | undefined;
    activeSetIndex: number;
    currentSet: WorkoutSet;
    completedSetsCount: number;
    totalSets: number;
    totalVolume: number;
    progressPercent: number;
}

// ============================================================
// UTILITY TYPES
// ============================================================

export interface WorkoutProviderProps {
    item: {
        id: string;
        title?: string;
        exercises?: Exercise[];
        workoutDuration?: number;
    };
    onUpdate: (id: string, updates: Record<string, unknown>) => void;
    onExit: () => void;
    children: React.ReactNode;
}

export interface WorkoutSummaryData {
    session: WorkoutSession;
    duration: number;
    totalSets: number;
    totalVolume: number;
    prs: PersonalRecord[];
}

// ============================================================
// HAPTIC PATTERNS
// ============================================================

export const HAPTIC_PATTERNS = {
    TAP: [20],
    SET_COMPLETE: [50, 50, 50],
    REST_END: [200, 100, 200],
    PR_ACHIEVED: [100, 50, 100, 50, 200],
    SUCCESS: [50, 50, 100],
} as const;

// ============================================================
// INITIAL STATE FACTORY
// ============================================================

export const createInitialState = (
    exercises: Exercise[] = [],
    workoutDuration: number = 0,
    appSettings: AppSettings
): WorkoutState => {
    const now = Date.now();

    return {
        exercises,
        currentExerciseIndex: 0,

        startTimestamp: now - workoutDuration * 1000,
        totalPausedTime: 0,
        lastPauseTimestamp: null,
        isPaused: false,

        restTimer: { active: false, endTime: null, totalTime: 0, timeLeft: 0 },

        showSettings: false,
        showExerciseSelector: false,
        showQuickForm: false,
        showExerciseLibrary: false,
        isDrawerOpen: false,
        numpad: { isOpen: false, target: null, value: '' },

        showGoalSelector: false,
        showWarmup: false,
        showCooldown: false,
        showWaterReminder: false,
        showTutorial: false,
        showAICoach: false,

        tutorialExercise: null,
        showConfetti: false,
        showPRCelebration: null,

        appSettings,
        previousExerciseData: null,
        pendingHaptic: null,
    };
};
